import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import Draw, { createBox } from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';

import { setTranslateInteraction } from './annotation/translate.interaction';
import { setModifyInteraction } from './annotation/modify.interaction';
import {
	activeSquareRef,
	annotationClassId,
	isFeatureIntersectingSquare,
	isSelectableBox,
	annotationStyles,
	stampAnnotationNode,
	setAnnotationSource,
	notifyBoxSelected,
} from './annotation/annotation.interaction';
import {
	OBJECT_MODE,
	createSelectFirstSession,
	getActiveSelectFirst,
	subscribeSelectFirstMode,
} from './annotation/selectFirst';
import { getSquareCenter } from './freeHand.draw';

const defaultDrawTool = 'Box';

export { OBJECT_MODE };

let objectInteraction,
	objectLayer,
	snapInteraction,
	modifyInteraction,
	translateInteraction,
	activeDrawTool = defaultDrawTool;
let annotationClassIds = [];
let colorOptions = null;

const objectSource = new VectorSource();

let addedObjects = [];
let modifiedObjects = [];
// Whether boxes can be drawn and edited right now: off while no tile is open.
let objectDrawingActive = true;

// The scoped object session (Collection view): Draw and Select modes, a box is
// edited only once selected. The select-first controller lives in
// annotation/selectFirst.js; everything below is inert unless `scopedSession`.
let scopedSession = false;
let rotateEnabled = false;
let selectFirst = null;

/** A box of the active object inside the open tile: the only kind that can be selected, moved or reshaped. */
export const isSelectable = isSelectableBox;

/** Which interactions are on, from the open tile (legacy: all of them; scoped: the controller decides). */
const applyInteractionState = () => {
	if (scopedSession) {
		selectFirst?.setActive(objectDrawingActive);
		return;
	}
	objectInteraction?.setActive(objectDrawingActive);
	snapInteraction?.setActive(objectDrawingActive);
	modifyInteraction?.setActive(objectDrawingActive);
	translateInteraction?.setActive(objectDrawingActive);
};

/** Take a box out of the legacy per-tile lists; the returned function puts it back. */
const forgetBox = feature => {
	const wasAdded = forgetFeature(feature);
	return () => {
		const tileName = feature.get('tileName');
		if (wasAdded && tileName) {
			(addedObjects[tileName] ??= []).push(feature);
		}
	};
};

export const startObjectAnnotation = ({
	colorOptionsInput,
	existingObjects,
	squareList,
	mapReference,
	scoped = false,
}) => {
	colorOptions = colorOptionsInput;
	scopedSession = scoped;
	objectLayer = new VectorLayer({
		source: objectSource,
		zIndex: 25,
		style: (feature, resolution) => annotationStyles({ feature, colorOptions, resolution }),
	});
	loadObjects(existingObjects, squareList);

	snapInteraction = new Snap({
		source: objectSource,
	});

	mapReference.addLayer(objectLayer);
	mapReference.addInteraction(snapInteraction);
	setAnnotationSource(objectSource);

	if (scoped) {
		selectFirst = createSelectFirstSession({
			map: mapReference,
			source: objectSource,
			layer: objectLayer,
			isSelectable,
			updateModified: updateModifiedObjects,
			forgetBox,
			rotateEnabled,
			active: objectDrawingActive,
			snap: snapInteraction,
		});
		translateInteraction = selectFirst.getTranslate();
		modifyInteraction = selectFirst.getModify();
	} else {
		translateInteraction = setTranslateInteraction({
			layer: objectLayer,
			updateModifiedObjects,
		});
		mapReference.addInteraction(translateInteraction);

		modifyInteraction = setModifyInteraction({
			source: objectSource,
			updateModifiedObjects,
		});
		mapReference.addInteraction(modifyInteraction);

		// The box being touched is the one the label chips act on.
		modifyInteraction.on('modifystart', e => notifyBoxSelected(e.features.item(0)));
		translateInteraction.on('translatestart', e => notifyBoxSelected(e.features.item(0)));
	}

	setDrawInteraction({
		mapReference,
		drawTool: 'Box',
	});
	applyInteractionState();
};

// The scoped session is reached through the registry, so the keys and the toolbar
// work the same for the orthophoto and the single image. They are inert (draw mode,
// nothing to dismiss) while no scoped session runs.

export const getObjectMode = () => getActiveSelectFirst()?.getMode() ?? OBJECT_MODE.DRAW;

/** Hear about mode changes (the toolbar follows a click-select); returns the unsubscribe. */
export const subscribeObjectMode = subscribeSelectFirstMode;

/**
 * Switch the scoped session between drawing and selecting. Draw to Select keeps
 * the selected box; Select to Draw clears it. No-op outside a scoped session.
 */
export const setObjectMode = mode => getActiveSelectFirst()?.setMode(mode);

/**
 * Turn rotation on or off while reshaping: Modify is rebuilt with it. The value
 * is kept for a session that starts later.
 */
export const setObjectRotateEnabled = enabled => {
	rotateEnabled = Boolean(enabled);
	getActiveSelectFirst()?.setRotateEnabled(rotateEnabled);
};

/** Abandon the box being sketched, if any. */
export const cancelSketch = () => getActiveSelectFirst()?.cancelSketch() ?? false;

/** Whether a box is being sketched right now in the scoped session. */
export const isObjectSketching = () => getActiveSelectFirst()?.isSketching() ?? false;

/**
 * Esc inside the scoped session: first the sketch, then the selection.
 * @returns {'sketch'|'selection'|null} what it dismissed; null means nothing was left to dismiss
 */
export const handleEscape = () => getActiveSelectFirst()?.handleEscape() ?? null;

export const setDrawInteraction = ({ mapReference, drawTool = null }) => {
	if (objectInteraction) {
		mapReference.removeInteraction(objectInteraction);
	}

	if (drawTool) {
		activeDrawTool = drawTool;
	}

	objectInteraction = new Draw({
		source: objectSource,
		type: 'Circle',
		geometryFunction: activeDrawTool === 'Box' ? createBox() : undefined,
		style: feature =>
			annotationStyles({
				feature,
				activeClassId: annotationClassId,
				colorOptions,
			}),
		// A press on a selectable box belongs to selecting it, never to a new sketch.
		...(scopedSession && { condition: selectFirst.drawCondition }),
	});
	if (scopedSession) {
		// Tracks the sketch and the undo entry of a drawn box, and sets Draw on or off by mode.
		selectFirst.attachDraw(objectInteraction);
	} else {
		objectInteraction.setActive(objectDrawingActive);
	}

	mapReference.addInteraction(objectInteraction);

	objectInteraction.on('drawstart', function (e) {
		const currentFeature = e.feature;
		// A new box ends any relabelling of an old one.
		notifyBoxSelected(null);

		let isInside = isFeatureIntersectingSquare(currentFeature, activeSquareRef);

		if (isInside) {
			const currentSquareName = activeSquareRef?.properties.tile_name;
			const addedObject = e.feature;
			addedObject.values_.classId = annotationClassId;
			addedObject.fileName = currentSquareName;
			stampAnnotationNode(addedObject);
			// The tile a box belongs to, for the session helpers (fileName is the legacy key).
			addedObject.set('tileName', currentSquareName, true);

			annotationClassIds.push(annotationClassId);
			if (!scopedSession) {
				snapInteraction.setActive(true);
				modifyInteraction.setActive(true);
			}
		} else {
			objectInteraction.abortDrawing(); // This does not trigger drawend (Feature will not be added to layer)
			if (!scopedSession) {
				snapInteraction.setActive(false);
				modifyInteraction.setActive(false);
			}

			// moves view to the square that the user is trying to draw in
			mapReference.getView().animate({
				center: getSquareCenter(activeSquareRef.geometry.coordinates[0]),
				zoom: 21.5,
				duration: 300,
			});
		}
	});

	objectInteraction.on('drawend', function (e) {
		const currentSquareName = activeSquareRef.properties.tile_name;
		const addedObject = e.feature;

		if (addedObjects[currentSquareName] === undefined) {
			addedObjects[currentSquareName] = [];
		}
		addedObjects[currentSquareName].push(addedObject);
	});
};

/** Take a box out of the legacy per-tile lists; true when it was in the added one. */
const forgetFeature = feature => {
	let wasAdded = false;
	Object.values(addedObjects).forEach(list => {
		const index = list.findIndex(box => box.ol_uid === feature.ol_uid);
		if (index !== -1) {
			list.splice(index, 1);
			wasAdded = true;
		}
	});
	return wasAdded;
};

/**
 * Remove the selected box of the scoped session, and remember how to bring it
 * back. A removed model box is recorded as a reject by the session's own
 * `removefeature` listener; the undo drops that reject again.
 * @returns {{ feature: Feature, entry: { undo: () => void } }|null} null when nothing is selected
 */
export const removeSelectedBox = () => getActiveSelectFirst()?.removeSelectedBox() ?? null;

// helper function that enables user to delete a object on click
const deleteObject = feature => {
	const currentSquareName = activeSquareRef.properties.tile_name;

	// A box of an object session (restored, loaded from the list, or a model box the
	// user touched) is in neither list below: erase it directly.
	if (feature.get('nodeUuid') && (feature.get('saved') || feature.get('source') === 'model')) {
		objectSource.removeFeature(feature);
		return;
	}

	if (addedObjects[currentSquareName] !== undefined) {
		addedObjects[currentSquareName].forEach((circle, index) => {
			if (circle.ol_uid === feature.ol_uid) {
				console.log('deleted from addedObjects');
				addedObjects[currentSquareName].splice(index, 1);
				objectSource.removeFeature(feature);
				return;
			}
		});
	}

	if (modifiedObjects[currentSquareName] !== undefined) {
		modifiedObjects[currentSquareName].forEach((circle, index) => {
			if (circle.values_.id === feature.values_.id) {
				console.log('deleted from modified circles');
				modifiedObjects[currentSquareName].splice(index, 1);
				objectSource.removeFeature(feature);
				return;
			}
		});
	}
};

/**
 * Turn drawing and editing on or off, keeping the interactions on the map. Off
 * while no tile is open in a tile-driven session, so a click on the map picks
 * a tile instead of starting a box.
 */
export const setObjectDrawingActive = active => {
	objectDrawingActive = active;
	// A scoped session started elsewhere (the single image) is switched too.
	const session = getActiveSelectFirst();
	if (session && session !== selectFirst) session.setActive(active);
	applyInteractionState();
};

export const getObjectEraseData = () => {
	return {
		interactionsToDeactivate: [
			objectInteraction,
			snapInteraction,
			modifyInteraction,
			translateInteraction,
		],
		selectedLayer: objectLayer,
		deleteFunction: deleteObject,
	};
};

export const getObjectsInSquare = (square, taskId, addToast) => {
	const allPreparedObjects = prepareObjectData(taskId, addToast);
	if (!allPreparedObjects) return null;

	const objectsInSquare = [];
	let hasOverlappedAnnotations = false; // Only need to check this if there are no objects in the square

	allPreparedObjects.forEach(feature => {
		if (feature.tile_name === square.properties.tile_name) {
			objectsInSquare.push(feature);
		}
	});

	// No objects in square, checking for overlapped annotations
	if (objectsInSquare.length === 0) {
		allPreparedObjects.some(feature => {
			hasOverlappedAnnotations = isFeatureIntersectingSquare(feature, square);
			return hasOverlappedAnnotations; // This will stop the loop if hasOverlappedAnnotations is true
		});
	}

	return { annotations: objectsInSquare, hasOverlappedAnnotations };
};

/**
 * Stops drawing objects and returns the objects array
 * @returns {Array} an array containing the objects
 */
export const stopDrawingObjects = mapReference => {
	if (scopedSession) {
		selectFirst?.stop();
		selectFirst = null;
	}
	// An object session's listeners would record every box as erased and re-check
	// Save for each: a fast clear skips the per-box events. Model view keeps its clear.
	objectLayer.getSource().clear(Boolean(scopedSession));
	setAnnotationSource(null);
	mapReference.removeLayer(objectLayer);
	mapReference.removeInteraction(objectInteraction);
	mapReference.removeInteraction(snapInteraction);
	mapReference.removeInteraction(modifyInteraction);
	mapReference.removeInteraction(translateInteraction);

	// zooms out to the default zoom level
	mapReference.getView().animate({
		zoom: 17,
		duration: 300,
	});

	//Set all variables to default
	objectInteraction = null;
	objectLayer = null;
	snapInteraction = null;
	modifyInteraction = null;
	translateInteraction = null;
	objectDrawingActive = true;
	activeDrawTool = defaultDrawTool;
	annotationClassIds = [];
	colorOptions = null;
	addedObjects = [];
	modifiedObjects = [];
	scopedSession = false;
	rotateEnabled = false;

	return true;
};

/**
 * Removes the features from a specific square
 * @param {String} tile_name - the name of the tile
 * @returns {void}
 */
export const removeAllObjectsInSquare = tile_name => {
	let features = objectSource.getFeatures();
	features?.forEach(feature => {
		if (feature.fileName === tile_name) {
			objectSource.removeFeature(feature);
		}
	});
};

/**
 * loads the objects from the database
 * @param {Array} objects - an array of objects
 * @returns {void}
 * @todo add correct file name to the objects
 */
const loadObjects = (objects, squareList) => {
	let objectsFeatures = [];

	objects?.forEach(feature => {
		try {
			const { geometries, properties } = feature;
			const { center, classid: classId, radius, annotation_uuid: id } = properties;
			const { x, y } = center;

			const objectFeature = new Feature({
				id,
				classId,
				geometry: new Polygon(geometries.bounding_box.coordinates),
				radius,
				center: [x, y],
			});

			objectsFeatures.push(objectFeature);
			objectSource.addFeature(objectFeature);
			annotationClassIds.push(classId);
		} catch (error) {
			console.error('Could not add existing detection as annotation to map', error);
		}
	});

	squareList?.forEach(square => {
		const squareName = square.properties.tile_name;
		if (modifiedObjects[squareName] === undefined) {
			modifiedObjects[squareName] = [];
		}
		objectsFeatures.forEach(feature => {
			const isInside = isFeatureIntersectingSquare(feature, square);
			if (isInside) {
				feature.values_.squareName = squareName;
				modifiedObjects[squareName].push(feature);
			}
		});
	});
	return objectsFeatures;
};

const prepareObjectData = (taskId, addToast) => {
	let preparedAddedObjects = [];
	let preparedModifiedObjects = [];

	function extractFeatureData(feature, squareName) {
		const modelType = feature.getGeometry().getType().toLowerCase();
		let featureData = {
			classId: feature.values_.classId,
			tile_name: squareName,
		};

		if (modelType === 'circle') {
			featureData.radius = feature.getGeometry().getRadius();
			featureData.center = feature.getGeometry().getCenter();
			featureData.annotation_type = 'circle';

			if (!featureData.radius || !featureData.center?.[0] || !featureData.center?.[1]) {
				throw new Error('Error extracting feature data');
			}
		} else if (modelType === 'polygon') {
			featureData.coordinates = feature.getGeometry().getCoordinates()[0];
			featureData.annotation_type = 'bbox';

			if (!featureData.coordinates) {
				throw new Error('Error extracting feature data');
			}
		}

		return featureData;
	}

	let errorFeatures = [];

	if (Object.keys(addedObjects).length > 0) {
		for (const squareName in addedObjects) {
			addedObjects[squareName].forEach(feature => {
				try {
					const featureData = extractFeatureData(feature, squareName);
					preparedAddedObjects.push(featureData);
				} catch (error) {
					console.error('Error extracting feature data from added objects', error);
					errorFeatures.push(feature);
				}
			});
		}
	}

	if (Object.keys(modifiedObjects).length > 0) {
		for (const squareName in modifiedObjects) {
			modifiedObjects[squareName].forEach(feature => {
				try {
					const featureData = extractFeatureData(feature, squareName);
					featureData.id = feature.values_.id; // Add the id for modified objects
					preparedModifiedObjects.push(featureData);
				} catch (error) {
					console.error('Error extracting feature data from modified objects', error);
					errorFeatures.push(feature);
				}
			});
		}
	}

	if (errorFeatures.length > 0) {
		for (let i = 0; i < errorFeatures.length; i++) {
			objectSource.removeFeature(errorFeatures[i]);
		}
		addToast({
			id: 'annotation-extract-error',
			bg: 'danger',
			title: 'Some annotations could not be extracted and will be removed from the map. Please re-draw them and try again.',
		});

		return null;
	}

	const results = {
		new: preparedAddedObjects,
		modified: preparedModifiedObjects,
	};

	let postData = preparePostData(results, taskId);

	return postData;
};

const createAnnotation = (annotation, taskId, isModified = false) => {
	const modelType = annotation.annotation_type.toLowerCase();
	const square = annotation.tile_name;
	const classId = annotation.classId;

	let baseAnnotation = {
		uuid: isModified ? annotation.id : 'unknown',
		task_uuid: taskId,
		annotation_type: modelType,
		machine_annotation: isModified,
		deprecated: false,
		human_input: true,
		classid: classId,
		tile_name: square,
	};

	if (modelType === 'circle') {
		const radius = annotation.radius;
		const center = annotation.center;
		const x = center[0];
		const y = center[1];

		return {
			...baseAnnotation,
			x_center: x,
			y_center: y,
			radius: radius,
		};
	} else if (modelType === 'bbox') {
		const coordinates = annotation.coordinates;
		return {
			...baseAnnotation,
			coordinates: coordinates,
		};
	}

	return baseAnnotation;
};

const preparePostData = (data, taskId) => {
	let postData = [];

	if (data.new && data.new.length > 0) {
		for (let i = 0; i < data.new.length; i++) {
			const newAnnotation = createAnnotation(data.new[i], taskId);
			postData.push(newAnnotation);
		}
	}

	if (data.deleted && data.deleted.length > 0) {
		for (let i = 0; i < data.deleted.length; i++) {
			console.log('deleted: ', data.deleted[i]);
		}
	}

	if (data.modified && data.modified.length > 0) {
		for (let i = 0; i < data.modified.length; i++) {
			const modifiedAnnotation = createAnnotation(data.modified[i], taskId, true);
			postData.push(modifiedAnnotation);
		}
	}

	return postData;
};

const updateModifiedObjects = currentFeature => {
	// A moved or reshaped box is no longer what the session saved.
	if (currentFeature?.get('saved')) currentFeature.set('saved', false);

	// If the feature has an id then it is a modified object
	// Update the modifiedObjects array with the new feature
	if (currentFeature?.values_?.id !== undefined) {
		const currentSquareName = activeSquareRef?.properties?.tile_name;

		if (!modifiedObjects[currentSquareName]) {
			modifiedObjects[currentSquareName] = [];
		}

		const currentSquareObjects = modifiedObjects[currentSquareName];
		const objectIndex = currentSquareObjects.findIndex(
			object => object.values_.id === currentFeature.values_.id
		);

		if (objectIndex !== -1) {
			currentSquareObjects[objectIndex] = currentFeature;
			console.log('replaced in modifiedObjects');
			return;
		}

		currentSquareObjects.push(currentFeature);
		console.log('added to modifiedObjects');
	}
};
